import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Zap, Calendar as CalendarIcon } from 'lucide-react';

interface RoomBooking {
  startTime: string;
  endTime: string;
  attendees: number;
  bookedBy: string;
  bookingTime: Date;
}

interface Room {
  id: string;
  name: string;
  status: 'available' | 'occupied' | 'booked';
  capacity: number;
  currentOccupants: number;
  features: string[];
  bookings: RoomBooking[];
  sensorData: {
    activePaths: number;
    lastActivity: Date | null;
    averageOccupancy: number;
  };
}

interface MeetingRoomAnalyticsProps {
  onRoomUpdate?: (rooms: Room[]) => void;
  newBooking?: RoomBooking & { roomId: string };
}

const MeetingRoomAnalytics: React.FC<MeetingRoomAnalyticsProps> = ({ 
  onRoomUpdate,
  newBooking 
}) => {
  const [rooms, setRooms] = useState<Room[]>([
    {
      id: 'CONF_A',
      name: 'Boardroom A',
      status: 'available',
      capacity: 16,
      currentOccupants: 0,
      features: ['Video conferencing', 'Whiteboard', 'Display', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 0,
        lastActivity: null,
        averageOccupancy: 0
      }
    },
    {
      id: 'CONF_B',
      name: 'Boardroom B',
      status: 'available',
      capacity: 16,
      currentOccupants: 0,
      features: ['Video conferencing', 'Whiteboard', 'Display', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 0,
        lastActivity: null,
        averageOccupancy: 0
      }
    },
    {
      id: 'CONF_1',
      name: 'Conference 1',
      status: 'occupied',
      capacity: 12,
      currentOccupants: 6,
      features: ['Display', 'Whiteboard', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 3,
        lastActivity: new Date(),
        averageOccupancy: 6
      }
    },
    {
      id: 'CONF_2',
      name: 'Conference 2',
      status: 'occupied',
      capacity: 12,
      currentOccupants: 10,
      features: ['Display', 'Whiteboard', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 5,
        lastActivity: new Date(),
        averageOccupancy: 8
      }
    },
    {
      id: 'FOCUS_1',
      name: 'Focus Room 1',
      status: 'available',
      capacity: 4,
      currentOccupants: 0,
      features: ['Display', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 0,
        lastActivity: null,
        averageOccupancy: 0
      }
    },
    {
      id: 'FOCUS_2',
      name: 'Focus Room 2',
      status: 'booked',
      capacity: 4,
      currentOccupants: 0,
      features: ['Display', 'Climate control'],
      bookings: [],
      sensorData: {
        activePaths: 0,
        lastActivity: new Date(Date.now() - 3600000), // 1 hour ago
        averageOccupancy: 0
      }
    }
  ]);

  useEffect(() => {
    if (newBooking) {
      setRooms(prevRooms => {
        const updatedRooms = prevRooms.map(room => {
          if (room.id === newBooking.roomId) {
            const updatedBookings = [...room.bookings, {
              startTime: newBooking.startTime,
              endTime: newBooking.endTime,
              attendees: newBooking.attendees,
              bookedBy: newBooking.bookedBy,
              bookingTime: newBooking.bookingTime
            }];
            
            // Sort bookings by start time
            updatedBookings.sort((a, b) => 
              new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
            );

            return {
              ...room,
              status: 'booked' as const,
              bookings: updatedBookings
            };
          }
          return room;
        });

        // Notify parent of room updates if callback provided
        if (onRoomUpdate) {
          onRoomUpdate(updatedRooms);
        }

        return updatedRooms;
      });
    }
  }, [newBooking, onRoomUpdate]);

  const analytics = {
    totalRooms: rooms.length,
    currentlyOccupied: rooms.filter(room => room.status === 'occupied').length,
    averageUtilization: 72,
    noShowRate: 15,
    upcomingMeetings: [
      { room: 'Boardroom A', time: '11:00 AM', attendees: 8, duration: 60 },
      { room: 'Focus Room 2', time: '11:30 AM', attendees: 4, duration: 30 },
      { room: 'Conference 3', time: '1:00 PM', attendees: 12, duration: 90 },
    ]
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'occupied':
        return { bg: '#fee', color: '#c33', border: '#fcc' };
      case 'booked':
        return { bg: '#fef9e7', color: '#8a7355', border: '#e8dfd0' };
      case 'available':
        return { bg: 'var(--sca-badge-bg)', color: 'var(--sca-teal)', border: 'var(--sca-teal-muted)' };
      default:
        return { bg: '#f5f5f5', color: '#666', border: '#ddd' };
    }
  };

  const getTimeRangeString = (startTime: string, endTime: string) => {
    return `${startTime} - ${endTime}`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold" style={{ color: 'var(--sca-ink)' }}>Meeting Room Analytics</h2>
        <p className="text-sm mt-1" style={{ color: 'var(--sca-muted)' }}>Real-time room status and utilization</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center mb-2">
            <Calendar className="mr-2" style={{ color: 'var(--sca-teal)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--sca-ink)' }}>Room Usage</h3>
          </div>
          <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{analytics.currentlyOccupied}/{analytics.totalRooms}</p>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Rooms currently in use</p>
        </div>

        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center mb-2">
            <Zap className="mr-2" style={{ color: 'var(--sca-teal)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--sca-ink)' }}>Utilization</h3>
          </div>
          <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{analytics.averageUtilization}%</p>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Average daily usage</p>
        </div>

        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center mb-2">
            <Clock className="mr-2" style={{ color: '#c33' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--sca-ink)' }}>No-Show Rate</h3>
          </div>
          <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{analytics.noShowRate}%</p>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Of booked meetings</p>
        </div>

        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center mb-2">
            <Users className="mr-2" style={{ color: 'var(--sca-teal-muted)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--sca-ink)' }}>Capacity Usage</h3>
          </div>
          <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>64%</p>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Of total seats</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Upcoming Meetings</h3>
          <div className="space-y-4">
            {analytics.upcomingMeetings.map((meeting, index) => (
              <div key={index} className="flex items-center justify-between pb-2" style={{ borderBottom: '1px solid var(--sca-border)' }}>
                <div>
                  <p className="font-medium" style={{ color: 'var(--sca-ink)' }}>{meeting.room}</p>
                  <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>{meeting.time} ({meeting.duration} min)</p>
                </div>
                <div className="flex items-center" style={{ color: 'var(--sca-ink)' }}>
                  <Users size={16} className="mr-1" />
                  <span>{meeting.attendees}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl shadow-sm p-6 border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Room Status</h3>
          <div className="space-y-4">
            {rooms.map((room) => {
              const statusColors = getStatusColor(room.status);
              return (
                <div key={room.id} className="pb-4 last:border-b-0" style={{ borderBottom: '1px solid var(--sca-border)' }}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--sca-ink)' }}>{room.name}</p>
                      <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                        Capacity: {room.capacity} | Current: {room.currentOccupants}
                        {room.sensorData.activePaths > 0 && (
                          <span className="ml-2" style={{ color: 'var(--sca-teal)' }}>
                            {room.sensorData.activePaths} active paths
                          </span>
                        )}
                      </p>
                      {room.status === 'booked' && !room.sensorData.activePaths && room.sensorData.lastActivity && (
                        <p className="text-sm" style={{ color: '#8a7355' }}>
                          No activity detected since {new Date(room.sensorData.lastActivity).toLocaleTimeString()}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-1">
                        {room.features.map((feature, idx) => (
                          <span 
                            key={idx} 
                            className="text-xs px-2 py-1 rounded" 
                            style={{ backgroundColor: 'var(--sca-warm-chip)', color: 'var(--sca-warm-text)' }}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-medium" 
                      style={{ 
                        backgroundColor: statusColors.bg, 
                        color: statusColors.color,
                        border: `1px solid ${statusColors.border}`
                      }}
                    >
                      {room.status.charAt(0).toUpperCase() + room.status.slice(1)}
                    </span>
                  </div>
                  {room.bookings.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium" style={{ color: 'var(--sca-muted)' }}>Today's Bookings:</p>
                      {room.bookings.map((booking, idx) => (
                        <div key={idx} className="text-sm flex items-center mt-1" style={{ color: 'var(--sca-muted)' }}>
                          <CalendarIcon size={14} className="mr-1" />
                          {getTimeRangeString(booking.startTime, booking.endTime)}
                          <Users size={14} className="ml-2 mr-1" />
                          {booking.attendees}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRoomAnalytics; 